/**
 * Claiming extra time (DESIGN-toil.md screen 3) — or, with `forUser`, a manager or admin
 * recording it directly (screen 5, approved at once). An admin's own claim is approved at
 * once too (assumption 7), so it gets the rate picker as well.
 *
 * Assumption 1: the day is today or earlier, at most 24 hours. The server says the same.
 */
import React, { useEffect, useState } from 'react';
import { Calendar, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toilApi } from '../../lib/api';
import { fmtToil, givenMinutes, toMinutes } from '../../lib/toil';
import RatePicker from './RatePicker';

export default function ClaimToilDialog({ open, onOpenChange, onDone, forUser, withRate, usualRate }) {
  const [day, setDay] = useState(null);
  const [dayOpen, setDayOpen] = useState(false);
  const [hours, setHours] = useState('');
  const [mins, setMins] = useState('');
  const [note, setNote] = useState('');
  const [rate, setRate] = useState(usualRate || '1');
  const [customH, setCustomH] = useState('');
  const [customM, setCustomM] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDay(null); setHours(''); setMins(''); setNote('');
      setRate(usualRate || '1'); setCustomH(''); setCustomM('');
    }
  }, [open, usualRate]);

  const worked = toMinutes(hours, mins);
  const custom = toMinutes(customH, customM);
  const given = withRate ? givenMinutes(worked, rate, custom) : null;
  const recording = !!forUser;

  const save = async () => {
    if (!day) { toast.error('Choose the day the extra time was worked'); return; }
    if (!worked) { toast.error('Enter the extra time worked'); return; }
    if (worked > 24 * 60) { toast.error('A claim is for one day — at most 24 hours'); return; }
    if (withRate && !given) { toast.error('Enter the time to give back'); return; }
    setSaving(true);
    try {
      const res = await toilApi.claim({
        worked_on: format(day, 'yyyy-MM-dd'),
        worked_minutes: worked,
        note: note.trim() || null,
        ...(recording ? { user_id: forUser.id } : {}),
        ...(withRate ? { rate, custom_minutes: rate === 'custom' ? custom : null } : {}),
      });
      const d = res.data || {};
      if (d.how === 'claimed') {
        toast.success(d.sent_to && d.sent_to.length
          ? `Claim sent to ${d.sent_to.join(' or ')}`
          : 'Claim sent for sign-off');
      } else {
        toast.success(`${d.given} of time in lieu added`);
      }
      onOpenChange(false);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the claim');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="toil-claim-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {recording ? `Record time in lieu for ${forUser.name}` : 'Claim time in lieu'}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">For extra time already worked.</p>

        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label>Day worked</Label>
            <Popover open={dayOpen} onOpenChange={setDayOpen}>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="input-hive w-full justify-start font-normal" data-testid="toil-day">
                  <Calendar className="w-4 h-4 mr-2" />
                  {day ? format(day, 'EEE dd/MM/yyyy') : 'Pick the day'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single" selected={day}
                  onSelect={(d) => { setDay(d); setDayOpen(false); }}
                  disabled={(d) => d > new Date()}
                  defaultMonth={day || new Date()} initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Extra time worked</Label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" max="24" inputMode="numeric" value={hours}
                onChange={(e) => setHours(e.target.value)} data-testid="toil-hours"
                className="input-hive w-20 rounded-md border border-input bg-background px-2 py-1.5 text-sm" aria-label="Hours worked" />
              <span className="text-sm text-muted-foreground">hours</span>
              <input type="number" min="0" max="59" step="5" inputMode="numeric" value={mins}
                onChange={(e) => setMins(e.target.value)} data-testid="toil-minutes"
                className="input-hive w-20 rounded-md border border-input bg-background px-2 py-1.5 text-sm" aria-label="Minutes worked" />
              <span className="text-sm text-muted-foreground">minutes</span>
            </div>
          </div>

          {withRate && (
            <div className="space-y-2">
              <Label>Rate</Label>
              <RatePicker worked={worked} rate={rate} onRate={setRate} usual={usualRate}
                customH={customH} customM={customM} onCustom={(h, m) => { setCustomH(h); setCustomM(m); }} />
            </div>
          )}

          <div className="space-y-2">
            <Label>{recording ? 'Note' : 'What was it for?'}</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              className="input-hive resize-none" maxLength={500} data-testid="toil-note" />
            <p className="text-xs text-muted-foreground">
              Seen by {recording ? 'them, ' : 'you, '}the people who sign off their time in lieu, and admins.
            </p>
          </div>

          {!withRate && (
            <p className="text-sm text-muted-foreground">
              Your approver chooses the rate when they sign it off, so the time you get back may be more than this.
            </p>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="button" className="btn-primary" onClick={save} disabled={saving} data-testid="toil-claim-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                : withRate ? (given ? `Add ${fmtToil(given)}` : 'Add') : 'Send claim'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
